import { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShoppingCart, Plus, Minus, X, Truck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CartDrawer({ cart, setCart, onCheckout }) {
  const [open, setOpen] = useState(false);
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const count = cart.reduce((sum, item) => sum + item.quantity, 0);

  const updateQty = (id, size, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id === id && item.size === size) {
        const newQty = item.quantity + delta;
        return newQty <= 0 ? null : { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  return (
    <>
      <button
        data-testid="cart-toggle"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[80] bg-foreground text-background w-14 h-14 flex items-center justify-center shadow-lg hover:bg-primary hover:text-foreground transition-colors"
      >
        <ShoppingCart size={22} />
        {count > 0 && (
          <span className="absolute -top-2 -right-2 bg-[#8B3020] text-[#E8DFC8] font-heading text-xs w-6 h-6 flex items-center justify-center">
            {count}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-background shadow-xl overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl uppercase">Your Cart</h2>
                <button onClick={() => setOpen(false)} className="text-foreground hover:text-primary"><X size={24} /></button>
              </div>

              {cart.length === 0 ? (
                <p className="font-body text-sm text-muted-foreground py-8 text-center">Cart is empty.</p>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map((item, idx) => (
                      <div key={`${item.product_id}-${item.size}-${idx}`} className="flex gap-4 bg-muted p-3">
                        {item.image_url && (
                          <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover bg-foreground" />
                        )}
                        <div className="flex-1">
                          <p className="font-heading text-sm uppercase">{item.name}</p>
                          {item.size && <p className="font-body text-[10px] text-muted-foreground">Size: {item.size}</p>}
                          <p className="font-heading text-sm text-primary">${item.price.toFixed(2)}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <button onClick={() => updateQty(item.product_id, item.size, -1)} className="w-6 h-6 bg-foreground text-background flex items-center justify-center"><Minus size={12} /></button>
                            <span className="font-body text-sm w-6 text-center">{item.quantity}</span>
                            <button onClick={() => updateQty(item.product_id, item.size, 1)} className="w-6 h-6 bg-foreground text-background flex items-center justify-center"><Plus size={12} /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t-2 border-foreground pt-4 mb-6">
                    <div className="flex justify-between font-heading text-lg uppercase">
                      <span>Total</span>
                      <span>${total.toFixed(2)}</span>
                    </div>
                  </div>

                  <Button
                    data-testid="checkout-btn"
                    onClick={() => { setOpen(false); onCheckout(); }}
                    className="w-full rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase h-12 text-base border-none"
                  >
                    <Truck size={16} className="mr-2" /> Checkout
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CheckoutModal({ cart, setCart, onClose }) {
  const [form, setForm] = useState({ customer_name: "", customer_email: "", shipping_address: "" });
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.shipping_address) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        ...form,
        items: cart.map(item => ({ product_id: item.product_id, quantity: item.quantity, size: item.size }))
      });
      setOrderComplete(data);
      setCart([]);
      toast.success("Order placed!");
    } catch {
      toast.error("Failed to place order. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative bg-background w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 sm:p-8 shadow-xl" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-foreground hover:text-primary"><X size={24} /></button>

        {orderComplete ? (
          <div className="text-center py-8">
            <div className="inline-block bg-foreground px-6 py-4 mb-6">
              <p className="font-heading text-xl text-[#B09030] uppercase">Order Confirmed</p>
            </div>
            <p className="font-heading text-2xl uppercase mb-2">Thank You!</p>
            <p className="font-body text-sm text-muted-foreground mb-4">
              Your order has been placed. We'll reach out to confirm payment and shipping details.
            </p>
            <div className="bg-muted p-4 text-left mb-4">
              <p className="font-body text-xs text-muted-foreground uppercase tracking-wider mb-1">Order ID</p>
              <p className="font-body text-sm">{orderComplete.id?.slice(0, 8).toUpperCase()}</p>
              <p className="font-body text-xs text-muted-foreground uppercase tracking-wider mt-3 mb-1">Total</p>
              <p className="font-heading text-lg">${orderComplete.total?.toFixed(2)}</p>
            </div>
            <p className="font-body text-xs text-muted-foreground">
              Payment instructions will be sent to {orderComplete.customer_email}
            </p>
            <Button onClick={onClose} className="mt-6 rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase h-10 border-none">
              Close
            </Button>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl uppercase mb-6">Checkout</h2>

            {/* Order summary */}
            <div className="bg-muted p-4 mb-6">
              <p className="font-heading text-sm uppercase mb-3">Order Summary</p>
              {cart.map((item, idx) => (
                <div key={idx} className="flex justify-between font-body text-sm py-1">
                  <span>{item.name} {item.size && `(${item.size})`} x{item.quantity}</span>
                  <span>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t border-foreground/20 mt-2 pt-2 flex justify-between font-heading text-base uppercase">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="font-heading text-sm uppercase block mb-2">Full Name *</label>
                <Input
                  data-testid="checkout-name"
                  value={form.customer_name}
                  onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                  placeholder="Your full name"
                  className="rounded-none border-none bg-muted font-body"
                />
              </div>
              <div>
                <label className="font-heading text-sm uppercase block mb-2">Email *</label>
                <Input
                  data-testid="checkout-email"
                  type="email"
                  value={form.customer_email}
                  onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                  placeholder="your@email.com"
                  className="rounded-none border-none bg-muted font-body"
                />
              </div>
              <div>
                <label className="font-heading text-sm uppercase block mb-2">Shipping Address *</label>
                <Textarea
                  data-testid="checkout-address"
                  value={form.shipping_address}
                  onChange={e => setForm(f => ({ ...f, shipping_address: e.target.value }))}
                  placeholder="Street, City, State, ZIP"
                  rows={3}
                  className="rounded-none border-none bg-muted font-body"
                />
              </div>
              <div className="bg-muted p-3">
                <p className="font-body text-xs text-muted-foreground">
                  Payment will be arranged after order confirmation. PayPal payment link coming soon.
                </p>
              </div>
              <Button
                type="submit"
                data-testid="place-order-btn"
                disabled={loading}
                className="w-full rounded-none bg-foreground text-background hover:bg-primary hover:text-foreground font-heading uppercase h-12 text-base border-none"
              >
                {loading ? "Placing Order..." : "Place Order"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function MerchPage() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState({});

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data.filter(p => p.in_stock))).catch(() => {});
  }, []);

  const addToCart = (product) => {
    const size = selectedSizes[product.id] || "";
    const sizes = product.sizes ? product.sizes.split(",").map(s => s.trim()).filter(Boolean) : [];
    if (sizes.length > 0 && !size) {
      toast.error("Please select a size.");
      return;
    }
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id && i.size === size);
      if (existing) {
        return prev.map(i => i.product_id === product.id && i.size === size ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { product_id: product.id, name: product.name, price: product.price, image_url: product.image_url, size, quantity: 1 }];
    });
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <div data-testid="merch-page" className="aged-paper">
      {/* Header */}
      <section className="py-16 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="segment-label">Official Gear</div>
            <div className="flex-1 h-[1px] bg-foreground/15" />
          </div>
          <h1 data-testid="merch-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase mb-4">
            Merchandise
          </h1>
          <div className="h-[3px] w-16 gold-accent" />
        </div>
      </section>

      <div className="vintage-divider" />

      <section className="py-12 sm:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Payment notice */}
          <div className="bg-[#0D0D0D] p-5 mb-10 flex items-start gap-4">
            <ShoppingCart size={20} className="text-[#B09030] flex-shrink-0 mt-1" />
            <div>
              <p className="font-heading text-sm text-[#B09030] uppercase mb-1">How to Order</p>
              <p className="font-body text-sm text-[#E8DFC8]/70">
                Online payments are not yet active. To place an order, contact us directly at{" "}
                <a href="mailto:Piedmontwrestlingclub@Gmail.com" className="text-[#B09030] hover:underline">Piedmontwrestlingclub@Gmail.com</a>{" "}
                or message us on{" "}
                <a href="https://facebook.com/Piedmontwrestlingclub" target="_blank" rel="noopener noreferrer" className="text-[#B09030] hover:underline">Facebook</a>{" / "}
                <a href="https://instagram.com/Piedmontwrestlingclub" target="_blank" rel="noopener noreferrer" className="text-[#B09030] hover:underline">Instagram</a>.
              </p>
            </div>
          </div>
          {products.length === 0 ? (
            <div className="py-24 text-center">
              <div className="inline-block bg-[#0D0D0D] px-8 py-5 mb-8">
                <p className="font-heading text-xl sm:text-2xl text-[#B09030] uppercase tracking-wider">
                  Merch Store
                </p>
              </div>
              <p data-testid="merch-coming-soon" className="font-heading text-2xl sm:text-3xl uppercase leading-snug mb-8">
                Gear drops soon.
              </p>
              <div className="h-[3px] w-16 gold-accent mx-auto mb-8" />
              <p className="font-body text-sm text-muted-foreground max-w-md mx-auto">
                Official Piedmont Wrestling Club merchandise is on the way.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map(product => {
                const sizes = product.sizes ? product.sizes.split(",").map(s => s.trim()).filter(Boolean) : [];
                return (
                  <div key={product.id} data-testid={`product-card-${product.id}`} className="group">
                    <div className="bg-[#0D0D0D] aspect-square overflow-hidden relative">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <p className="font-heading text-4xl text-[#B09030]/20">{product.name.charAt(0)}</p>
                        </div>
                      )}
                      {product.category && product.category !== "General" && (
                        <div className="absolute top-3 left-3 segment-label">{product.category}</div>
                      )}
                    </div>

                    <div className="bg-[#0D0D0D] p-4 border-t-[3px] border-[#B09030]">
                      <h3 className="font-heading text-lg text-[#E8DFC8] uppercase">{product.name}</h3>
                      <p className="font-heading text-xl text-[#B09030] mt-1">${product.price.toFixed(2)}</p>
                      {product.description && (
                        <p className="font-body text-xs text-[#E8DFC8]/50 mt-2 leading-relaxed">{product.description}</p>
                      )}

                      {/* Size selector */}
                      {sizes.length > 0 && (
                        <div className="flex gap-2 mt-3 flex-wrap">
                          {sizes.map(s => (
                            <button
                              key={s}
                              onClick={() => setSelectedSizes(prev => ({ ...prev, [product.id]: s }))}
                              className={`font-body text-xs px-3 py-1 transition-colors ${
                                selectedSizes[product.id] === s
                                  ? "bg-[#B09030] text-[#0D0D0D]"
                                  : "bg-[#E8DFC8]/10 text-[#E8DFC8]/60 hover:bg-[#E8DFC8]/20"
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Add to cart disabled until payment integration */}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Cart and checkout disabled until payment integration */}
      {/* <CartDrawer cart={cart} setCart={setCart} onCheckout={() => setShowCheckout(true)} /> */}
      {/* {showCheckout && <CheckoutModal cart={cart} setCart={setCart} onClose={() => setShowCheckout(false)} />} */}
    </div>
  );
}
